import Dialog from "../../General/ConfirmableDialog";
import {
  useQueryClient } from "@tanstack/react-query";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  TextField,
} from "@mui/material";

interface ConfirmActionButtonProps {
  id: string;
  entityType: string;
  actionType: "delete" | "restore";
  entityLabel: string; // Label for the confirmation message (e.g., "driver", "asset")
  queryKey: string; // Query key to invalidate (e.g., "drivers", "repairs")
  buttonColor?: "error" | "success";
  buttonLabel?: string;
  queryParam?: object;
  requireTerminationDate?: boolean;
  terminationDateLabel?: string;
}

const ConfirmActionButton = ({
  id,
  entityType,
  actionType,
  entityLabel,
  queryKey,
  buttonColor = "error",
  buttonLabel = "Confirm",
  queryParam,
  requireTerminationDate = false,
  terminationDateLabel = "Termination Date",
}: ConfirmActionButtonProps) => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [open, setOpen] = useState(false);
  const [terminationDate, setTerminationDate] = useState("");

  const handleClose = () => {
    setOpen(false);
    setStatus("idle");
    setError("");
    setTerminationDate("");
  };

  const handleAction = async () => {
    if (actionType === "delete" && requireTerminationDate && !terminationDate) {
      setStatus("error");
      setError(`${terminationDateLabel} is required`);
      return;
    }
    setLoading(true);
    setStatus("idle");
    setError("");
    try {
      const url = `${apiVariable}/${actionType}${entityType}/${id}`;
      if (actionType === "delete") {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };
        const config: {
          headers: Record<string, string>;
          params?: object;
          data?: string;
        } = {
          headers,
          params: queryParam,
        };

        if (requireTerminationDate) {
          config.data = JSON.stringify(terminationDate);
          config.headers["Content-Type"] = "application/json";
        }

        await axios.delete(url, config);
      } else {
        await axios.put(
          url,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setStatus("success");
      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (error: any) {
      const responseData = error.response?.data;
      const errorMessage =
        typeof responseData === "string"
          ? responseData
          : responseData?.message ||
            responseData?.error ||
            (Array.isArray(responseData?.errors)
              ? responseData.errors.join(", ")
              : undefined) ||
            `Failed to ${actionType} ${entityLabel}`;
      setError(errorMessage);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        color={buttonColor}
        onClick={(event) => {
          setOpen(true);
          setStatus("idle");
          setError("");
          event.stopPropagation();
        }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : buttonLabel}
      </Button>

      <Dialog open={open} onClose={loading ? undefined : handleClose}>
        <Box
          padding={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          {status !== "idle" && (
            <Alert severity={status} variant="filled" sx={{ width: "100%" }}>
              {status === "success"
                ? `${entityLabel} ${actionType}d successfully`
                : error}
            </Alert>
          )}
          <Typography variant="h6" gutterBottom>
            Are you sure you want to {actionType} this {entityLabel}?
          </Typography>
          {actionType === "delete" && requireTerminationDate && (
            <TextField
              label={terminationDateLabel}
              type="datetime-local"
              value={terminationDate}
              onChange={(event) => setTerminationDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={status === "error" && !terminationDate}
              helperText={
                status === "error" && !terminationDate
                  ? `${terminationDateLabel} is required`
                  : " "
              }
            />
          )}
          <Box display="flex" gap={1}>
            <Button
              onClick={handleAction}
              color={buttonColor}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Yes"}
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              disabled={loading}
            >
              No
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ConfirmActionButton;
